import React, {useCallback} from 'react';
import styled from 'styled-components';
import {CatalogItemCard} from '../../../common/ui/CatalogItemCard';
import {RemoteDataUI} from '../../../common/ui/RemoteDataUI';
import {Spinner} from 'react-bootstrap';
import {Indicador} from '../../../ui';
import {PriceWithFallback} from '../../../common/ui/PriceWithFallback';
import {usePendingOrders} from '../../../product/application/usePendingOrders';
import {AddRestockedToCartButton} from '../../../product/ui/AddRestockedToCartButton';

/**
 * UI constants
 */
const COLORS = {
    primaryText: '#007bff',
    lightAlertBackground: '#f8f9fa',
    dangerAlertBackground: '#f8d7da',
};

const SPACING = {
    sectionPadding: '1.5rem',
    cardGap: '1.5rem',
};

/**
 * Styled components
 */
const Container = styled.div`
    display: flex;
    gap: ${SPACING.sectionPadding};
    flex-direction: column;
`;

const ScrollArea = styled.div`
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 0.25rem;
`;

const GridWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${SPACING.cardGap};
    box-sizing: border-box;
`;

const CardWrapper = styled.div`
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Alert = styled.div`
    background-color: ${({ type }) =>
            type === 'light' ? COLORS.lightAlertBackground : COLORS.dangerAlertBackground};
    padding: ${SPACING.cardGap};
    border-radius: 0.5rem;
    font-size: 1rem;
`;

const CenteredSpinner = styled.div`
    display: flex;
    justify-content: center;
    margin: 2rem 0;
`;

/**
 * PendingProductsOverview - Vista de "Productos pendientes" de la cuenta
 * (/cuenta/pendientes).
 *
 * Muestra los pendientes vivos del cliente con las unidades pendientes y, en los
 * que ya hay stock, el botón para añadirlos al carrito. Los repuestos se pintan
 * primero: son los accionables.
 *
 * @returns {JSX.Element}
 */
export const PendingProductsOverview = () => {
    const {pendingData, refresh, removeLocally} = usePendingOrders();

    const renderPendingOrder = useCallback((pendingOrder) => {
        const {product, pendingQuantity, isRestocked} = pendingOrder;
        return (
            <CardWrapper key={product.code}>
                <CatalogItemCard data={{ catalogItem: product }}>
                    <CatalogItemCard.Price>
                        <PriceWithFallback data={{
                            sellingPrice: product.price?.bestPrice,
                            basePrice: product.price?.value
                        }}/>
                    </CatalogItemCard.Price>
                </CatalogItemCard>
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <small className="text-muted">{pendingQuantity} ud. pendientes</small>
                    {isRestocked && (
                        <span className="badge bg-success">Ya hay stock</span>
                    )}
                </div>
                {isRestocked && (
                    <AddRestockedToCartButton
                        pendingOrder={pendingOrder}
                        onAdded={removeLocally}
                        onFailed={refresh}
                    />
                )}
            </CardWrapper>
        );
    }, [removeLocally, refresh]);

    return (
        <RemoteDataUI remoteData={pendingData}>
            <RemoteDataUI.Success>
                {(pendingOrders) => (
                    <Container>
                        <Indicador indicador="Productos pendientes" />
                        {pendingOrders.length === 0 ? (
                            <Alert type="light">No tienes productos pendientes.</Alert>
                        ) : (
                            <ScrollArea>
                                <GridWrapper>
                                    {[...pendingOrders]
                                        .sort((a, b) => Number(b.isRestocked) - Number(a.isRestocked))
                                        .map(renderPendingOrder)}
                                </GridWrapper>
                            </ScrollArea>
                        )}
                    </Container>
                )}
            </RemoteDataUI.Success>
            <RemoteDataUI.Pending>
                <CenteredSpinner>
                    <Spinner />
                </CenteredSpinner>
            </RemoteDataUI.Pending>
            <RemoteDataUI.Failure>
                <Alert type="danger">Imposible cargar productos.</Alert>
            </RemoteDataUI.Failure>
        </RemoteDataUI>
    );
};
